import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, Building2, Search, CheckCircle, Clock, XCircle, Phone, Mail, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { fmtDate } from '@/lib/utils';
import DepositRecognitionModal from '@/components/admin/DepositRecognitionModal';

const STATUS_CONFIG = {
  new: { label: 'NEW', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  open: { label: 'OPEN', icon: Users, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  resolved: { label: 'RESOLVED', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10' },
};

const EVENT_TYPE_LABELS = {
  staff_party: 'Staff Party',
  team_building: 'Team Building',
  trade_show: 'Trade Show',
  brand_activation: 'Brand Activation',
  other: 'Other',
};

export default function AdminGroupBookings() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const { data: enquiries = [], isLoading } = useQuery({
    queryKey: ['admin-group-bookings'],
    queryFn: () => base44.entities.GroupBookingEnquiry.list('-created_date', 500),
  });

  const q = search.toLowerCase();
  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      if (typeFilter !== 'all' && e.enquiry_type !== typeFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (!q) return true;
      return [
        e.contact_name, e.contact_email, e.contact_phone,
        e.company_name, e.special_requests, e.square_payment_id,
      ].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [enquiries, q, typeFilter, statusFilter]);

  const counts = useMemo(() => ({
    corporate: enquiries.filter((e) => e.enquiry_type === 'corporate').length,
    party: enquiries.filter((e) => e.enquiry_type === 'party').length,
    deposits: enquiries.filter((e) => e.deposit_paid).length,
    new: enquiries.filter((e) => e.status === 'new').length,
  }), [enquiries]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-group-bookings'] });

  const handleStatusChange = async (id, newStatus) => {
    await base44.entities.GroupBookingEnquiry.update(id, { status: newStatus });
    await refresh();
    setSelected((s) => (s?.id === id ? { ...s, status: newStatus } : s));
  };

  const handleSaveNotes = async (id, notes) => {
    await base44.entities.GroupBookingEnquiry.update(id, { staff_notes: notes });
    await refresh();
    setSelected((s) => (s?.id === id ? { ...s, staff_notes: notes } : s));
  };

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1';

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="border border-white/5 bg-[#111115] px-4 py-3.5">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">TOTAL ENQUIRIES</p>
          <p className="font-black text-xl mt-1 text-white">{enquiries.length}</p>
        </div>
        <div className="border border-white/5 bg-[#111115] px-4 py-3.5">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">CORPORATE (B2B)</p>
          <p className="font-black text-xl mt-1 text-white">{counts.corporate}</p>
        </div>
        <div className="border border-white/5 bg-[#111115] px-4 py-3.5">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">PARTIES (B2C)</p>
          <p className="font-black text-xl mt-1 text-white">{counts.party}</p>
        </div>
        <div className="border border-primary/20 bg-primary/5 px-4 py-3.5">
          <p className="text-primary text-[9px] font-bold tracking-widest">DEPOSITS PAID</p>
          <p className="font-black text-xl mt-1 text-primary">{counts.deposits}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company..."
            className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
          />
        </div>
        <div>
          <label className={labelClass}>TYPE</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="seo-input h-11 bg-[#111115] border border-white/10">
            <option value="all">All Types</option>
            <option value="corporate">Corporate (B2B)</option>
            <option value="party">Parties (B2C)</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>STATUS</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="seo-input h-11 bg-[#111115] border border-white/10">
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-white/5 bg-[#111115]">
          <Users className="w-10 h-10 text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-sm">No group booking enquiries found</p>
        </div>
      ) : (
        <div className="border border-white/5 overflow-hidden">
          <div className="flex items-center gap-4 px-5 py-3 bg-[#111115] border-b border-white/5">
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 shrink-0">DATE</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-20 shrink-0">TYPE</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest flex-1">CONTACT</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 text-center shrink-0">DEPOSIT</p>
            <p className="text-white/30 text-[10px] font-bold tracking-widest w-28 text-center shrink-0">STATUS</p>
          </div>
          {filtered.map((e) => {
            const status = STATUS_CONFIG[e.status] || STATUS_CONFIG.new;
            const StatusIcon = status.icon;
            return (
              <div
                key={e.id}
                onClick={() => setSelected(e)}
                className="flex items-center gap-4 px-5 py-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
              >
                <p className="text-white/40 text-xs w-20 shrink-0">{fmtDate(e.created_date) || '—'}</p>
                <div className="w-20 shrink-0">
                  <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 ${e.enquiry_type === 'corporate' ? 'text-blue-400 bg-blue-400/10' : 'text-primary bg-primary/10'}`}>
                    {e.enquiry_type === 'corporate' ? 'B2B' : 'B2C'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {e.contact_name || '—'}
                    {e.company_name && <span className="text-white/40 text-xs"> · {e.company_name}</span>}
                  </p>
                  <p className="text-white/40 text-xs truncate">{e.contact_email || '—'}</p>
                </div>
                <div className="w-28 text-center shrink-0 flex flex-col items-center gap-1">
                  {e.deposit_paid ? (
                    <span className="text-[9px] font-bold tracking-wider text-green-400 bg-green-400/10 px-2 py-0.5">£{Number(e.deposit_amount || 0).toFixed(0)} PAID</span>
                  ) : (
                    <span className="text-white/20 text-xs">—</span>
                  )}
                  {e.event_completed && (
                    <span className="text-[8px] font-bold tracking-wider text-blue-400 bg-blue-400/10 px-1.5 py-0.5">COMPLETE</span>
                  )}
                </div>
                <span className={`flex items-center gap-1 text-[9px] font-bold tracking-wider px-2 py-0.5 w-28 justify-center shrink-0 ${status.bg} ${status.color}`}>
                  <StatusIcon className="w-3 h-3" /> {status.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <EnquiryDetail
          enquiry={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          onSaveNotes={handleSaveNotes}
          onRecognize={refresh}
        />
      )}
    </div>
  );
}

function EnquiryDetail({ enquiry, onClose, onStatusChange, onSaveNotes, onRecognize }) {
  const [notes, setNotes] = useState(enquiry.staff_notes || '');
  const [showRecognize, setShowRecognize] = useState(false);

  const isCorporate = enquiry.enquiry_type === 'corporate';
  const status = STATUS_CONFIG[enquiry.status] || STATUS_CONFIG.new;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-2xl bg-[#0d0d0f] border border-white/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0d0d0f] border-b border-white/5 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isCorporate ? <Building2 className="w-5 h-5 text-blue-400" /> : <Users className="w-5 h-5 text-primary" />}
            <h3 className="text-white font-black text-sm uppercase tracking-wide">
              {isCorporate ? 'Corporate Enquiry' : 'Party Enquiry'}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Contact */}
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-2">CONTACT</p>
            <p className="text-white font-bold text-sm">{enquiry.contact_name}</p>
            <div className="flex flex-wrap gap-4 mt-1">
              <a href={`mailto:${enquiry.contact_email}`} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-primary transition-colors">
                <Mail className="w-3 h-3" /> {enquiry.contact_email}
              </a>
              {enquiry.contact_phone && (
                <a href={`tel:${enquiry.contact_phone}`} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-primary transition-colors">
                  <Phone className="w-3 h-3" /> {enquiry.contact_phone}
                </a>
              )}
            </div>
          </div>

          {/* Event details */}
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-2">EVENT DETAILS</p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {isCorporate ? (
                <>
                  {enquiry.company_name && <div><p className="text-white/30">Company</p><p className="text-white/70">{enquiry.company_name}</p></div>}
                  {enquiry.event_type && <div><p className="text-white/30">Event Type</p><p className="text-white/70">{EVENT_TYPE_LABELS[enquiry.event_type] || enquiry.event_type}</p></div>}
                  {enquiry.event_date && <div><p className="text-white/30">Event Date</p><p className="text-white/70">{fmtDate(enquiry.event_date)}</p></div>}
                  {enquiry.number_of_attendees && <div><p className="text-white/30">Attendees</p><p className="text-white/70">{enquiry.number_of_attendees}</p></div>}
                </>
              ) : (
                <>
                  {enquiry.party_type && <div><p className="text-white/30">Party Type</p><p className="text-white/70">{enquiry.party_type === 'kids' ? 'Kids (8–15)' : 'Adults (16+)'}</p></div>}
                  {enquiry.preferred_date && <div><p className="text-white/30">Date</p><p className="text-white/70">{fmtDate(enquiry.preferred_date)}</p></div>}
                  {enquiry.time_slot && <div><p className="text-white/30">Time</p><p className="text-white/70">{enquiry.time_slot}</p></div>}
                  {enquiry.number_of_drivers && <div><p className="text-white/30">Drivers</p><p className="text-white/70">{enquiry.number_of_drivers}</p></div>}
                  {enquiry.package_tier && <div><p className="text-white/30">Package</p><p className="text-white/70">{enquiry.package_tier}</p></div>}
                  {enquiry.birthday_child_name && <div><p className="text-white/30">Birthday Child</p><p className="text-white/70">{enquiry.birthday_child_name}{enquiry.birthday_child_age ? ` (age ${enquiry.birthday_child_age})` : ''}</p></div>}
                  {enquiry.estimated_headcount && <div><p className="text-white/30">Est. Headcount</p><p className="text-white/70">{enquiry.estimated_headcount}</p></div>}
                </>
              )}
            </div>
          </div>

          {/* Deposit */}
          {enquiry.deposit_paid && (
            <div className="border border-green-400/20 bg-green-400/5 p-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-green-400 text-[10px] font-bold tracking-widest">DEPOSIT RECEIVED</p>
                {enquiry.event_completed ? (
                  <span className="text-[8px] font-bold tracking-wider text-blue-400 bg-blue-400/10 px-2 py-0.5">EVENT COMPLETE</span>
                ) : (
                  <button
                    onClick={() => setShowRecognize(true)}
                    className="text-[9px] font-bold tracking-wider text-primary hover:text-primary/80 transition-colors"
                  >
                    COMPLETE EVENT + RECORD BALANCE →
                  </button>
                )}
              </div>
              <p className="text-white text-sm font-bold">£{Number(enquiry.deposit_amount || 0).toFixed(2)}</p>
              {enquiry.square_payment_id && <p className="text-white/30 text-xs mt-1 font-mono">Square: {enquiry.square_payment_id}</p>}
              <div className="mt-2 pt-2 border-t border-white/5">
                {enquiry.balance_paid ? (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-green-400 bg-green-400/10 px-2 py-0.5">BALANCE PAID</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-yellow-400 bg-yellow-400/10 px-2 py-0.5">BALANCE PENDING</span>
                )}
                {(enquiry.final_headcount || enquiry.estimated_headcount) && (
                  <p className="text-white/50 text-xs mt-1.5"><span className="text-white/30">Headcount:</span> {enquiry.final_headcount || enquiry.estimated_headcount} drivers</p>
                )}
                {enquiry.balance_paid && (
                  <div className="mt-1.5 text-xs space-y-1">
                    <p className="text-white/50"><span className="text-white/30">Balance:</span> £{Number(enquiry.balance_amount || 0).toFixed(2)}</p>
                    {enquiry.balance_payment_method && <p className="text-white/50"><span className="text-white/30">Method:</span> {enquiry.balance_payment_method}</p>}
                    {enquiry.balance_payment_ref && <p className="text-white/50 font-mono"><span className="text-white/30 non-mono">Ref:</span> {enquiry.balance_payment_ref}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Special requests */}
          {enquiry.special_requests && (
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest mb-2">SPECIAL REQUESTS</p>
              <p className="text-white/60 text-xs leading-relaxed bg-[#111115] p-3 border border-white/5">{enquiry.special_requests}</p>
            </div>
          )}

          {/* Staff notes */}
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-2">STAFF NOTES</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add internal notes..."
              className="seo-input w-full h-auto py-2 bg-[#111115] border border-white/10"
            />
            <button
              onClick={() => onSaveNotes(enquiry.id, notes)}
              className="mt-2 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 py-1.5 transition-colors"
            >
              SAVE NOTES
            </button>
          </div>

          {/* Status */}
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-2">STATUS</p>
            <div className="flex gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => onStatusChange(enquiry.id, key)}
                  className={`text-[10px] font-bold tracking-wider px-3 py-1.5 border transition-colors ${
                    enquiry.status === key
                      ? 'border-primary/50 bg-primary/10 text-primary'
                      : 'border-white/10 text-white/40 hover:text-white hover:border-white/30'
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showRecognize && (
        <DepositRecognitionModal
          enquiry={enquiry}
          onClose={() => setShowRecognize(false)}
          onDone={() => { setShowRecognize(false); onRecognize(); onClose(); }}
        />
      )}
    </div>
  );
}